// Well Profile completion criteria — single source of truth.
//
// Extracted verbatim from WellProfileEditor.jsx so the Dashboard's
// "Well Profiles" setup badge and the editor's own per-section badges can
// never disagree about what "complete" means. The editor imports these;
// it no longer carries its own copy.
//
// Why this exists at all: validateWellProfile() alone is NOT a completeness
// test. Classification and Status ship with non-empty schema defaults
// (wellType, wellPurpose, environment, bopLocation, status="planned"), so a
// well where only the Identity section was ever saved still passes
// whole-record validation — which is why the Dashboard used to show
// COMPLETE for it.

import { listBhasForWell } from "@/lib/bha/storage";
import { listDrillStringsForWell } from "@/lib/drillstring/storage";
import { listKillSheetsForWell } from "@/lib/killsheet/storage";
import { listMudProgramsForWell } from "@/lib/mud/program";

// Sections that must actually be satisfied (real data, not just "saved") for
// the Well Profile to be considered complete. BOP is required only when the
// well is offshore/subsea — see sectionRequired below — so it isn't listed
// here. Everything else (Location, Tubing, Notes, Casing, BHA, Drill String,
// Mud Program, Kill Sheet) is genuinely optional: saving the Well Profile
// must never depend on them.
export const WELL_REQUIRED_SECTIONS = new Set(["identity", "classification", "depths", "status"]);

export function sectionRequired(rec, key) {
  if (key === "bop") return rec.environment === "offshore" || rec.bopLocation === "subsea";
  return WELL_REQUIRED_SECTIONS.has(key);
}

export function sectionHasData(rec, key) {
  const s = (v) => typeof v === "string" && v.trim().length > 0;
  const n = (v) => v !== "" && v != null && Number.isFinite(Number(v));
  switch (key) {
    case "identity":       return s(rec.wellName);
    // Classification/Status ship with non-empty schema defaults (wellType,
    // wellPurpose, environment, bopLocation, status="planned"), so a plain
    // field-presence check is satisfied before the operator ever opens the
    // section. Also require an explicit Save of THIS section (sectionsTouched)
    // so the badge reflects a real review, not just the untouched default.
    case "classification": return s(rec.wellType) && s(rec.wellPurpose) && s(rec.environment) && s(rec.bopLocation) && !!rec.sectionsTouched?.classification;
    case "depths":         return n(rec.totalDepthPlannedFt) || n(rec.totalDepthCurrentFt) || n(rec.totalDepthFinalFt);
    case "location":       return n(rec.latitude) && n(rec.longitude);
    case "status":         return s(rec.status) && !!rec.sectionsTouched?.status;
    case "bop":            return s(rec.bopRefId);
    case "casing": {
      // Root-cause fix (Reference Library Return-Workflow QA) — an empty
      // placeholder row added via "Add string" was previously enough to
      // flip this badge to Completed before the operator picked an actual
      // casing spec. Require at least one row to carry real identifying
      // data: a library reference, a nominal size, or a manually entered
      // casing dimension (a library link is optional).
      const rows = rec.children?.casingProgram || [];
      return rows.some((r) => s(r.casingRefId) || s(r.nominalSize)
        || s(r.manualInnerDiameterIn) || s(r.manualOuterDiameterIn));
    }
    // BHA / DrillString completeness is read from their own storage. Because
    // reading from storage inside a pure function is fine (small localStorage
    // arrays) and this helper is called on every render for a saved well only.
    case "bha":            return listBhasForWell(rec.id || "").length > 0;
    case "drillstring":    return listDrillStringsForWell(rec.id || "").length > 0;
    case "mud":            return listMudProgramsForWell(rec.id || "").length > 0;
    case "killsheet":      return listKillSheetsForWell(rec.id || "").length > 0;
    // Tubing has no editor yet (completion-phase, V2) — it can never carry data.
    case "tubing":         return false;
    case "notes":          return s(rec.notes);
    default:               return false;
  }
}

// The Well Profile data-entry flow, in order. SINGLE SOURCE OF TRUTH.
//
// Lives here rather than in WellProfileEditor because the child editors
// (Mud Program, BHA, Drill String, Kill Sheet) each navigate back to "the
// next section" after a save with ?return=well. Those used to hardcode a
// section id apiece, so reordering the flow silently sent operators to the
// wrong place — saving the Mud Program jumped to the Kill Sheet. Everything
// now derives the next step from this one array.
export const WELL_SECTION_FLOW = [
  { key: "identity",       label: "Identity" },
  { key: "classification", label: "Classification" },
  { key: "depths",         label: "Depths" },
  { key: "location",       label: "Location" },
  { key: "status",         label: "Status" },
  { key: "bop",            label: "BOP" },
  // Mud Program leads the engineering modules: Casing, BHA and the Kill
  // Sheet all inherit mud weight from it via RCE.
  { key: "mud",            label: "Mud Program" },
  { key: "casing",         label: "Casing" },
  { key: "bha",            label: "BHA" },
  { key: "drillstring",    label: "Drill String" },
  { key: "killsheet",      label: "Kill Sheet" },
  { key: "tubing",         label: "Tubing" },
  { key: "notes",          label: "Notes" },
];

/** The key of the section after `key`, or null at the end of the flow. */
export function nextWellSectionKey(key) {
  const i = WELL_SECTION_FLOW.findIndex((s) => s.key === key);
  if (i === -1 || i === WELL_SECTION_FLOW.length - 1) return null;
  return WELL_SECTION_FLOW[i + 1].key;
}

/** `/wells/:id#well-section-<next>` — where a child editor returns to. */
export function nextWellSectionHash(wellId, currentKey) {
  const next = nextWellSectionKey(currentKey);
  return next ? `/wells/${wellId}#well-section-${next}` : `/wells/${wellId}`;
}

// Sections the Kill Sheet workflow depends on. These are NOT required for
// the Well Profile record itself to be complete — a well can legitimately be
// finished without them — but a Kill Sheet cannot be produced until they
// carry data, so presenting them as merely "Optional" misleads the operator:
//   mud         → mud weight (kill calcs, BHA buoyancy)
//   casing      → casing ID for annular capacities
//   bha         → internal/annular volumes
//   drillstring → drill pipe geometry and capacities
//
// Deliberately kept OUT of WELL_REQUIRED_SECTIONS: widening that set would
// change isWellProfileComplete, and every existing well that is genuinely
// complete would start reading as in-progress on the Dashboard.
export const KILL_SHEET_PREREQUISITE_SECTIONS = ["mud", "casing", "bha", "drillstring"];

export function isKillSheetPrerequisite(key) {
  return KILL_SHEET_PREREQUISITE_SECTIONS.includes(key);
}

/** Prerequisite section keys that do not yet carry data, in workflow order. */
export function missingKillSheetPrerequisites(rec) {
  if (!rec || typeof rec !== "object") return [...KILL_SHEET_PREREQUISITE_SECTIONS];
  return KILL_SHEET_PREREQUISITE_SECTIONS.filter((key) => {
    try { return !sectionHasData(rec, key); } catch { return true; }
  });
}

/**
 * True when every REQUIRED section of this well carries real data — the
 * same bar the editor's own per-section "Completed" badges use.
 *
 * None of the required sections (identity / classification / depths /
 * status, plus bop when offshore-subsea) reads from storage, so this stays
 * cheap enough to run across every well profile on the Dashboard.
 */
export function isWellProfileComplete(rec) {
  if (!rec || typeof rec !== "object") return false;
  const keys = [...WELL_REQUIRED_SECTIONS];
  if (sectionRequired(rec, "bop")) keys.push("bop");
  return keys.every((key) => sectionHasData(rec, key));
}
